import React from 'react';
import styles from './CircleProgress.module.scss';

const CircleProgress = ({ percentage, label, size = 120, displayMode = 'percentage', correct = 0, total = 0, sectionType }) => {
    // Calculate score color based on percentage
    const getScoreColor = (percentage) => {
        if (percentage >= 80) return '#22c55e'; // Green
        if (percentage >= 60) return '#eab308'; // Yellow
        return '#ef4444'; // Red
    };

    const color = getScoreColor(percentage);
    const radius = (size - 4) / 2; // Maximum possible radius for optimal content space
    const circumference = 2 * Math.PI * radius;
    const strokeDasharray = circumference;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    // Convert SAT score to actual SAT scoring scale (200-800 per section, total 400-1600)
    const getSATScore = (correct, total) => {
        if (total === 0) return { math: 200, readingWriting: 200, total: 400 };

        // Approximate SAT scoring: Math (200-800), Reading & Writing (200-800)
        const mathQuestions = 44; // Typical SAT Math questions
        const readingWritingQuestions = 64; // Typical SAT Reading & Writing questions

        // Calculate proportional scores
        const mathCorrect = Math.round((correct / total) * mathQuestions * 0.5); // Approximate math portion
        const rwCorrect = Math.round((correct / total) * readingWritingQuestions * 0.5); // Approximate RW portion

        // Convert to SAT scale (simplified conversion)
        const mathScore = Math.min(800, Math.max(200, 200 + (mathCorrect / mathQuestions) * 600));
        const rwScore = Math.min(800, Math.max(200, 200 + (rwCorrect / readingWritingQuestions) * 600));

        return {
            math: Math.round(mathScore),
            readingWriting: Math.round(rwScore),
            total: Math.round(mathScore + rwScore)
        };
    };

    const satScore = getSATScore(correct, total);

    // Function to calculate individual SAT section score (200-800)
    const getSectionScore = (correct, total) => {
        if (!correct || !total) return 200;
        const percentage = Math.min(Math.max(correct / total, 0), 1);
        return Math.round(200 + (percentage * 600));
    };

    const getDisplayContent = () => {
        if (displayMode === 'sat-score') {
            return (
                <>
                    <div className={styles.satScore} style={{ color }}>
                        <div className={styles.totalScore}>{satScore.total}</div>
                        <div className={styles.scoreBreakdown}>
                            <div className={styles.sectionScore}>M: {satScore.math}</div>
                            <div className={styles.sectionScore}>RW: {satScore.readingWriting}</div>
                        </div>
                    </div>
                </>
            );
        } else if (displayMode === 'section-score') {
            const sectionScore = getSectionScore(correct, total);
            const scoreText = `${correct}/${total} (${percentage}%)`;
            return (
                <div className={styles.sectionScoreDisplay} style={{ color }}>
                    <div className={styles.individualScore}>{sectionScore}</div>
                    <div className={styles.scoreDetails}>{scoreText}</div>
                </div>
            );
        } else if (displayMode === 'fraction') {
            return (
                <div className={styles.fractionScore} style={{ color }}>
                    <div className={styles.fraction}>
                        <span className={styles.numerator}>{correct}</span>
                        <span className={styles.denominator}>/{total}</span>
                    </div>
                </div>
            );
        }
        // Default percentage mode
        return <div className={styles.percentage} style={{ color }}>{percentage}%</div>;
    };

    return (
        <div
            className={styles.circleProgress}
            style={{
                width: size,
                height: size,
                '--circle-size': `${size}px`,
                '--content-max-width': `${size * 0.7}px`
            }}
        >
            <svg width={size} height={size}>
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke="#f0f0f0"
                    strokeWidth="5"
                    fill="none"
                />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke={color}
                    strokeWidth="5"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={strokeDasharray}
                    strokeDashoffset={strokeDashoffset}
                    transform={`rotate(-90 ${size / 2} ${size / 2})`}
                    style={{ transition: 'stroke-dashoffset 0.5s ease-in-out' }}
                />
            </svg>
            <div className={styles.circleContent}>
                {getDisplayContent()}
                <div className={styles.label}>{label}</div>
            </div>
        </div>
    );
};

export default CircleProgress;